import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  UserPlus, Search, Pencil, Trash2, Send, ChevronDown, X,
  Shield, Eye, Layers, Clock, Building2, Check, Copy, Mail, KeyRound,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/constants";

const PERMISSION_LABELS = { view_only: "View only", edit: "Edit" };
const PERMISSION_COLORS = { view_only: "text-[color:var(--atech-text-muted)]", edit: "text-[color:var(--atech-copper)]" };

const inputCls =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)] focus:outline-none focus:border-[color:var(--atech-copper)]";

export default function AdminPortalUsers() {
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);    // null = add, obj = edit
  const [showModal, setShowModal] = useState(false);
  const [shareCreds, setShareCreds] = useState(null); // { name, email, password }
  const [resetUser, setResetUser] = useState(null);   // user whose password is being reset

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: users }, { data: projects }] = await Promise.all([
        api.get("/portal-users"),
        api.get("/projects"),
      ]);
      setUsers(users);
      setAllProjects(projects);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      (u.company || "").toLowerCase().includes(q)
    );
  });

  const handleDelete = async (u) => {
    if (!confirm(`Delete ${u.name} (${u.email})? This will remove their access to all projects.`)) return;
    try {
      await api.delete(`/portal-users/${u.id}`);
      load();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const handleResend = async (u) => {
    try {
      const { data } = await api.post(`/portal-users/${u.id}/resend-invite`);
      if (data.sent) alert(`Invite email sent to ${u.email}`);
      else alert(`Invite link: ${data.activation_url}`);
      load();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openAdd = () => { setEditUser(null); setShowModal(true); };
  const openEdit = (u) => { setEditUser(u); setShowModal(true); };

  return (
    <div className="fade-up" data-testid="admin-portal-users">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-1">Access management</div>
          <h2 className="font-display text-3xl tracking-tight text-[color:var(--atech-text)]">Portal users</h2>
          <p className="mt-1 text-sm text-[color:var(--atech-text-dim)] max-w-lg">
            Manage external users — clients, contractors, consultants and any other collaborators.
            Users can be assigned to specific projects across any client.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="btn-copper flex items-center gap-2 shrink-0"
          data-testid="add-portal-user-btn"
        >
          <UserPlus size={14} /> Add user
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total users", value: users.length },
          { label: "Edit permission", value: users.filter((u) => u.permission === "edit").length },
          { label: "Pending invite", value: users.filter((u) => u.status === "pending_invite" || u.status === "invited").length },
        ].map(({ label, value }) => (
          <div key={label} className="card-surface p-4 text-center">
            <div className="font-display text-2xl text-[color:var(--atech-text)]">{value}</div>
            <div className="label text-[color:var(--atech-text-muted)] mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or company…"
          className={`${inputCls} pl-9`}
          data-testid="portal-users-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)]">
            <X size={13} />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="portal-users-table">
            <thead>
              <tr className="border-b border-[color:var(--atech-border)]">
                {["Name", "Company", "Email", "Permission", "Projects", "Last online", "Added by", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left label text-[color:var(--atech-text-muted)] font-normal whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center label text-[color:var(--atech-text-muted)]">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center label text-[color:var(--atech-text-muted)]">
                  {search ? "No users match your search." : "No portal users yet. Click 'Add user' to create the first one."}
                </td></tr>
              )}
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onEdit={() => openEdit(u)}
                  onDelete={() => handleDelete(u)}
                  onResend={() => handleResend(u)}
                  onResetPassword={() => setResetUser(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <PortalUserModal
          user={editUser}
          allProjects={allProjects}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
          onCreatedWithPassword={(creds) => { setShowModal(false); load(); setShareCreds(creds); }}
        />
      )}

      {shareCreds && (
        <ShareCredentialsModal creds={shareCreds} onClose={() => setShareCreds(null)} />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
          onDone={(creds) => { setResetUser(null); setShareCreds(creds); }}
        />
      )}
    </div>
  );
}

function UserRow({ user, onEdit, onDelete, onResend, onResetPassword }) {
  const isPending = user.status === "pending_invite" || user.status === "invited";
  return (
    <tr className="border-b border-[color:var(--atech-border)]/50 hover:bg-white/[0.02] transition-colors" data-testid={`portal-user-row-${user.id}`}>
      <td className="px-4 py-3">
        <div className="text-[color:var(--atech-text)] font-medium">{user.name}</div>
        {isPending && (
          <span className="text-[10px] uppercase tracking-widest text-amber-500/80 font-mono">Pending invite</span>
        )}
      </td>
      <td className="px-4 py-3 text-[color:var(--atech-text-dim)]">{user.company || "—"}</td>
      <td className="px-4 py-3 text-[color:var(--atech-text-dim)] font-mono text-xs">{user.email}</td>
      <td className="px-4 py-3">
        <span className={`flex items-center gap-1.5 text-xs ${PERMISSION_COLORS[user.permission]}`}>
          {user.permission === "edit" ? <Shield size={12} /> : <Eye size={12} />}
          {PERMISSION_LABELS[user.permission] || user.permission}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="flex items-center gap-1.5 text-xs text-[color:var(--atech-text-dim)]">
          <Layers size={12} />
          {user.project_count ?? 0}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-[color:var(--atech-text-muted)] whitespace-nowrap">
        {user.last_login_at ? formatDate(user.last_login_at) : "Never"}
      </td>
      <td className="px-4 py-3 text-xs text-[color:var(--atech-text-muted)]">
        {user.added_by_name || "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          {isPending && (
            <button
              onClick={onResend}
              className="p-1.5 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-colors"
              title="Resend invite"
              data-testid={`resend-${user.id}`}
            >
              <Send size={13} />
            </button>
          )}
          <button
            onClick={onResetPassword}
            className="p-1.5 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-colors"
            title="Set / reset password"
            data-testid={`reset-password-${user.id}`}
          >
            <KeyRound size={13} />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)] transition-colors"
            title="Edit user"
            data-testid={`edit-portal-user-${user.id}`}
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-[color:var(--atech-text-muted)] hover:text-[#F87171] transition-colors"
            title="Delete user"
            data-testid={`delete-portal-user-${user.id}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Add / Edit user modal ────────────────────────────────────────────────────
function PortalUserModal({ user, allProjects, onClose, onSaved, onCreatedWithPassword }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    email: user?.email || "",
    permission: user?.permission || "view_only",
    project_ids: (user?.project_access || []).map((p) => (typeof p === "string" ? p : p.project_id)).filter(Boolean),
  });
  const [onboardMode, setOnboardMode] = useState("manual"); // "manual" | "invite"
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const genPassword = () => {
    // Friendly, easy-to-read temporary password
    const words = ["Copper", "Granary", "Harbour", "Willow", "Summit", "Meadow", "Beacon", "Cedar", "Atlas", "Orbit"];
    const w = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(1000 + Math.random() * 9000);
    setPassword(`${w}${n}!`);
    setShowPw(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isEdit) {
        await api.patch(`/portal-users/${user.id}`, {
          name: form.name,
          company: form.company || null,
          email: form.email,
          permission: form.permission,
          project_ids: form.project_ids,
        });
        onSaved();
        return;
      }

      const useManual = onboardMode === "manual";
      if (useManual && password.length < 8) {
        setErr("Password must be at least 8 characters.");
        setBusy(false);
        return;
      }
      await api.post("/portal-users", {
        name: form.name,
        company: form.company || null,
        email: form.email,
        permission: form.permission,
        project_ids: form.project_ids,
        ...(useManual ? { password } : {}),
      });
      if (useManual) {
        onCreatedWithPassword({ name: form.name, email: form.email.toLowerCase().trim(), password });
      } else {
        onSaved();
      }
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-10 overflow-y-auto" onClick={onClose}>
      <div className="card-surface w-full max-w-xl p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="portal-user-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[color:var(--atech-text)]">
            {isEdit ? `Edit — ${user.name}` : "Add portal user"}
          </h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"><X size={18} /></button>
        </div>

        {err && <div className="text-xs text-[#F87171] border border-[#F87171]/20 bg-[#F87171]/5 rounded-[2px] px-3 py-2 mb-5">{err}</div>}

        <form onSubmit={save} className="space-y-5" data-testid="portal-user-form">
          <div className="grid grid-cols-2 gap-4">
            <label className="block col-span-2 sm:col-span-1">
              <span className="label block mb-1.5">Full name *</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name-input" />
            </label>
            <label className="block col-span-2 sm:col-span-1">
              <span className="label block mb-1.5">Company</span>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Optional" className={inputCls} data-testid="user-company-input" />
            </label>
          </div>

          <label className="block">
            <span className="label block mb-1.5">Email address *</span>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="user-email-input" />
          </label>

          <div>
            <span className="label block mb-2">Permission</span>
            <div className="flex gap-4">
              {[{ v: "view_only", label: "View only", icon: Eye, desc: "Can view projects, documents and updates" },
                { v: "edit", label: "Edit", icon: Shield, desc: "Can also add and invite other users" }].map(({ v, label, icon: Icon, desc }) => (
                <label key={v} className={`flex-1 cursor-pointer rounded-[2px] border p-3.5 transition-colors ${form.permission === v ? "border-[color:var(--atech-copper)] bg-[rgba(198,132,88,0.06)]" : "border-[color:var(--atech-border)] hover:border-[color:var(--atech-border-strong)]"}`} data-testid={`perm-${v}`}>
                  <input type="radio" name="permission" value={v} checked={form.permission === v} onChange={() => setForm({ ...form, permission: v })} className="sr-only" />
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={13} className={form.permission === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text-muted)]"} />
                    <span className={`text-xs font-medium ${form.permission === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text)]"}`}>{label}</span>
                  </div>
                  <p className="text-[11px] text-[color:var(--atech-text-muted)]">{desc}</p>
                </label>
              ))}
            </div>
          </div>

          <div>
            <span className="label block mb-1.5">Active projects</span>
            <ProjectMultiSelect
              allProjects={allProjects}
              selected={form.project_ids}
              onChange={(ids) => setForm({ ...form, project_ids: ids })}
            />
            <div className="mt-1 text-[11px] text-[color:var(--atech-text-muted)]">
              {form.project_ids.length} project{form.project_ids.length !== 1 ? "s" : ""} selected
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-3">
              <span className="label block">How should they get access?</span>
              <div className="flex gap-3">
                {[
                  { v: "manual", label: "Set a password now", icon: KeyRound, desc: "Create the login yourself, then share it however you like" },
                  { v: "invite", label: "Send invite email", icon: Mail, desc: "Emails an activation link so they set their own password" },
                ].map(({ v, label, icon: Icon, desc }) => (
                  <label
                    key={v}
                    className={`flex-1 cursor-pointer rounded-[2px] border p-3.5 transition-colors ${onboardMode === v ? "border-[color:var(--atech-copper)] bg-[rgba(198,132,88,0.06)]" : "border-[color:var(--atech-border)] hover:border-[color:var(--atech-border-strong)]"}`}
                    data-testid={`onboard-${v}`}
                  >
                    <input type="radio" name="onboardMode" value={v} checked={onboardMode === v} onChange={() => setOnboardMode(v)} className="sr-only" />
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={13} className={onboardMode === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text-muted)]"} />
                      <span className={`text-xs font-medium ${onboardMode === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text)]"}`}>{label}</span>
                    </div>
                    <p className="text-[11px] text-[color:var(--atech-text-muted)]">{desc}</p>
                  </label>
                ))}
              </div>

              {onboardMode === "manual" ? (
                <label className="block">
                  <span className="label block mb-1.5">Temporary password *</span>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={8}
                        placeholder="At least 8 characters"
                        className={`${inputCls} pr-9`}
                        data-testid="user-password-input"
                      />
                      <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]" title={showPw ? "Hide" : "Show"}>
                        <Eye size={13} />
                      </button>
                    </div>
                    <button type="button" onClick={genPassword} className="shrink-0 text-xs px-3 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)]" data-testid="user-generate-password">
                      Generate
                    </button>
                  </div>
                  <span className="block mt-1.5 text-[11px] text-[color:var(--atech-text-muted)]">
                    After saving, you'll get a copy-paste summary to send them. They can change it from their Account page.
                  </span>
                </label>
              ) : (
                <div className="text-xs text-[color:var(--atech-text-muted)] bg-white/[0.03] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5">
                  An activation invite email will be sent automatically so the user can set their own password.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy} className="btn-copper" data-testid="save-portal-user">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Add user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reset / set password modal for an existing user ──────────────────────────
function ResetPasswordModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const genPassword = () => {
    const words = ["Copper", "Granary", "Harbour", "Willow", "Summit", "Meadow", "Beacon", "Cedar", "Atlas", "Orbit"];
    const w = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(1000 + Math.random() * 9000);
    setPassword(`${w}${n}!`);
    setShowPw(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/portal-users/${user.id}/set-password`, { password });
      onDone({ name: user.name, email: user.email, password });
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-10 overflow-y-auto" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl text-[color:var(--atech-text)]">Set a new password</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"><X size={18} /></button>
        </div>
        <p className="text-sm text-[color:var(--atech-text-dim)] mb-5">
          Set a fresh password for <strong className="text-[color:var(--atech-text)]">{user.name}</strong> ({user.email}).
          This replaces any existing password and any pending invite — they can sign in straight away.
        </p>
        {err && <div className="text-xs text-[#F87171] border border-[#F87171]/20 bg-[#F87171]/5 rounded-[2px] px-3 py-2 mb-4">{err}</div>}
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <label className="block">
            <span className="label block mb-1.5">New password *</span>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  placeholder="At least 8 characters"
                  className={`${inputCls} pr-9`}
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"><Eye size={13} /></button>
              </div>
              <button type="button" onClick={genPassword} className="shrink-0 text-xs px-3 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)]" data-testid="reset-password-generate">Generate</button>
            </div>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="reset-password-submit">
              {busy ? "Saving…" : "Set password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Share credentials modal (no-email onboarding) ────────────────────────────
export function ShareCredentialsModal({ creds, onClose }) {
  const [copied, setCopied] = useState("");
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(""), 1600); });
  };
  const loginUrl = `${window.location.origin}/login`;
  const summary =
    `Hi ${creds.name},\n\n` +
    `Your A-Tech portal login is ready:\n\n` +
    `Portal: ${loginUrl}\n` +
    `Email: ${creds.email}\n` +
    `Password: ${creds.password}\n\n` +
    `You can change your password from the Account page after signing in.`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-10 overflow-y-auto" onClick={onClose}>
      <div className="card-surface w-full max-w-lg p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="share-credentials-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl text-[color:var(--atech-text)]">Login created</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"><X size={18} /></button>
        </div>
        <p className="text-sm text-[color:var(--atech-text-dim)] mb-5">
          Send these details to <strong className="text-[color:var(--atech-text)]">{creds.name}</strong> from your own email or messaging app.
          They can sign in straight away and change the password from their Account page.
        </p>

        <div className="space-y-3">
          <CopyRow label="Portal address" value={loginUrl} copied={copied === "url"} onCopy={() => copy(loginUrl, "url")} mono />
          <CopyRow label="Email" value={creds.email} copied={copied === "email"} onCopy={() => copy(creds.email, "email")} mono />
          <CopyRow label="Password" value={creds.password} copied={copied === "pw"} onCopy={() => copy(creds.password, "pw")} mono />
        </div>

        <button
          onClick={() => copy(summary, "all")}
          className="mt-4 w-full text-sm flex items-center justify-center gap-2 px-3 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)]"
          data-testid="copy-credentials-summary"
        >
          {copied === "all" ? <><Check size={13} /> Message copied</> : <><Copy size={13} /> Copy a ready-to-send message</>}
        </button>

        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="btn-copper text-sm" data-testid="share-credentials-done">Done</button>
        </div>
      </div>
    </div>
  );
}

function CopyRow({ label, value, copied, onCopy, mono }) {
  return (
    <div className="border border-[color:var(--atech-border)] rounded-[2px] px-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)] mb-1">{label}</div>
        <div className={`text-sm text-[color:var(--atech-text)] break-all ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      <button
        onClick={onCopy}
        className="shrink-0 text-xs flex items-center gap-1.5 px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)]"
      >
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

// ─── Searchable multi-select for projects ─────────────────────────────────────
function ProjectMultiSelect({ allProjects, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const sel = new Set(selected);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const sorted = [...allProjects].sort((a, b) => {
    const aIn = sel.has(a.id);
    const bIn = sel.has(b.id);
    if (aIn !== bIn) return aIn ? -1 : 1;
    return a.project_name.localeCompare(b.project_name);
  });

  const filtered = sorted.filter((p) => !q || p.project_name.toLowerCase().includes(q.toLowerCase()) || (p.project_reference || "").toLowerCase().includes(q.toLowerCase()));

  const toggle = (id) => {
    const n = new Set(sel);
    n.has(id) ? n.delete(id) : n.add(id);
    onChange(Array.from(n));
  };

  const selectedProjects = allProjects.filter((p) => sel.has(p.id));

  return (
    <div ref={ref} className="relative" data-testid="project-multiselect">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 text-sm px-3 py-2.5 rounded-[2px] border transition-colors ${open ? "border-[color:var(--atech-copper)]" : "border-[color:var(--atech-border)]"} bg-[color:var(--atech-surface-elevated)] text-left`}
        data-testid="project-multiselect-trigger"
      >
        <span className="truncate text-[color:var(--atech-text-dim)]">
          {sel.size === 0 ? "No projects selected" : selectedProjects.slice(0, 2).map((p) => p.project_name).join(", ") + (sel.size > 2 ? ` +${sel.size - 2} more` : "")}
        </span>
        <ChevronDown size={13} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] shadow-2xl" data-testid="project-dropdown">
          <div className="p-2 border-b border-[color:var(--atech-border)]">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              className="w-full text-xs bg-transparent text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)] focus:outline-none px-1 py-0.5"
              data-testid="project-search-input"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-[color:var(--atech-text-muted)]">No projects match.</div>
            )}
            {filtered.map((p) => (
              <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-white/5" data-testid={`project-opt-${p.id}`}>
                <div className={`w-4 h-4 rounded-[1px] border flex items-center justify-center shrink-0 transition-colors ${sel.has(p.id) ? "bg-[color:var(--atech-copper)] border-[color:var(--atech-copper)]" : "border-[color:var(--atech-border)]"}`}>
                  {sel.has(p.id) && <Check size={10} className="text-white" />}
                </div>
                <input type="checkbox" className="sr-only" checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
                <span className="flex-1 min-w-0">
                  <span className="text-xs text-[color:var(--atech-text)] block truncate">{p.project_name}</span>
                  {p.project_reference && <span className="text-[10px] font-mono text-[color:var(--atech-text-muted)]">{p.project_reference}</span>}
                </span>
                {p.is_archived && <span className="text-[10px] text-[color:var(--atech-text-muted)] uppercase tracking-widest">Arch.</span>}
              </label>
            ))}
          </div>
          {sel.size > 0 && (
            <div className="px-3 py-2 border-t border-[color:var(--atech-border)] flex items-center justify-between">
              <span className="text-[11px] text-[color:var(--atech-text-muted)]">{sel.size} selected</span>
              <button type="button" onClick={() => onChange([])} className="text-[11px] text-[#F87171] hover:underline">Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
